/**
 * useGlobalAlarmSubscription — multi-site WebSocket subscription for the
 * customer-facing global Alarms hub.
 *
 * Joins every site room the caller has access to and invalidates the global
 * React Query cache (`['alarms', 'all']`) whenever an `alarm:transition`
 * event arrives on any of those rooms.
 *
 * Mirrors `useAlarmSubscription`'s contract (Phase 4 — `site:<uuid>` room,
 * identity-keyed payload) but spans the user's full site list.
 *
 * Why we DON'T `unsubscribe:site` on unmount:
 *   Per-site rooms are shared infrastructure — the per-site Alarms tab,
 *   SLD telemetry, and KPI charts all join the same rooms. Leaving them
 *   subscribed across navigation avoids race-y resubscribe churn on tab
 *   switching. This matches `useAlarmSubscription`'s behavior.
 *   Note: this invariant has exceptions — `useKPISubscription` and
 *   `useFleetTelemetry` both emit `unsubscribe:site` on unmount, so room
 *   persistence is not guaranteed while navigating away from surfaces that
 *   use those hooks; ref-counting to resolve this is tracked in issue #566.
 *
 * Cross-recall: also invalidates the legacy `['project', siteUuid]` keys for
 *   any site that emits a transition, so the per-site project page stays
 *   fresh if a user navigates from /alarms to /project/:id.
 *
 * site_code → UUID bridge (load-bearing):
 *   `alarm:transition` payloads carry `identity.site_id` as the wire
 *   site_code (e.g. 'HONDO-01'). The per-site React Query keys
 *   (`useSiteAlarms`) and route params use UUIDs from `config.sites.site_id`.
 *   The hook resolves code→UUID via `useSites()` before invalidating
 *   per-site keys; otherwise the cross-recall pre-warm is a silent no-op.
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getSocket } from '../services/socket';
import { useSites } from './useSites';
import type { AlarmTransitionEvent } from './useAlarmSubscription';

export function useGlobalAlarmSubscription(): void {
  const queryClient = useQueryClient();
  const sitesQ = useSites();

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    const sites = sitesQ.data?.sites ?? [];
    if (sites.length === 0) return;

    sites.forEach((s) => socket.emit('subscribe:site', s.site_id));

    // Map wire site_code → UUID for per-site cache invalidation. Without
    // this, `event.identity.site_id` (a code like 'HONDO-01') would never
    // match the UUID-keyed per-site React Query caches and the cross-recall
    // pre-warm would be a silent no-op.
    const codeToUuid = new Map<string, string>();
    for (const s of sites) {
      if (s.site_code) codeToUuid.set(s.site_code, s.site_id);
    }

    const handle = (event: AlarmTransitionEvent) => {
      queryClient.invalidateQueries({ queryKey: ['alarms', 'all'] });
      // Also invalidate the per-site keys so a future visit to the per-site
      // page reflects the latest state immediately. Identity carries the
      // wire site_code; resolve it to the UUID the per-site keys use.
      const siteCode = event?.identity?.site_id;
      if (!siteCode) return;
      const siteUuid = codeToUuid.get(siteCode);
      if (!siteUuid) return;
      queryClient.invalidateQueries({ queryKey: ['alarms', siteUuid] });
      queryClient.invalidateQueries({ queryKey: ['project', siteUuid] });
    };

    socket.on('alarm:transition', handle);

    return () => {
      socket.off('alarm:transition', handle);
    };
  }, [sitesQ.data, queryClient]);
}
